/**
 * decodeHtmlEntities — turn `&#8217;`, `&#x2019;`, `&#8220;`, `&#8211;`, `&amp;`
 * etc. into real characters. RSS/WordPress feeds encode punctuation as numeric
 * HTML entities, which is why scraped news titles showed raw codes like
 * "Spotify&#8217;s" in the middle of a title.
 *
 * Handles: decimal (`&#NNN;`), hex (`&#xHHH;`), and the common named entities.
 * Runs a second pass to catch double-encoded feeds (`&amp;#8217;`).
 */

const NAMED: Record<string, string> = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ",
  hellip: "…", mdash: "—", ndash: "–", minus: "−",
  rsquo: "’", lsquo: "‘", sbquo: "‚",
  rdquo: "”", ldquo: "“", bdquo: "„",
  laquo: "«", raquo: "»", lsaquo: "‹", rsaquo: "›",
  middot: "·", bull: "•", copy: "©", reg: "®", trade: "™",
  deg: "°", plusmn: "±", times: "×", divide: "÷",
  frac12: "½", frac14: "¼", frac34: "¾",
  eacute: "é", egrave: "è", agrave: "à", ccedil: "ç", uuml: "ü",
  ouml: "ö", auml: "ä", aring: "å", oslash: "ø", ntilde: "ñ",
  euro: "€", pound: "£", cent: "¢", yen: "¥", sect: "§", para: "¶",
  dagger: "†", Dagger: "‡", permil: "‰", prime: "′", Prime: "″",
};

function decodeOnce(input: string): string {
  return input.replace(/&(#x?[0-9a-f]+|[a-z][a-z0-9]*);/gi, (m, ent: string) => {
    if (ent[0] === "#") {
      const isHex = ent[1] === "x" || ent[1] === "X";
      const code = isHex ? parseInt(ent.slice(2), 16) : parseInt(ent.slice(1), 10);
      if (Number.isFinite(code) && code > 0 && code <= 0x10ffff) {
        try { return String.fromCodePoint(code); } catch { return m; }
      }
      return m;
    }
    // Try exact case first (for the few case-sensitive names), then lowercase.
    if (Object.prototype.hasOwnProperty.call(NAMED, ent)) return NAMED[ent];
    const low = ent.toLowerCase();
    return Object.prototype.hasOwnProperty.call(NAMED, low) ? NAMED[low] : m;
  });
}

export function decodeHtmlEntities(input: string | null | undefined): string {
  if (!input || typeof input !== "string") return input ?? "";
  if (input.indexOf("&") === -1) return input; // fast path — nothing to decode
  const once = decodeOnce(input);
  // Second pass only if a double-encoded entity remains.
  return once.indexOf("&") !== -1 ? decodeOnce(once) : once;
}
